/**
 * Harmony Snackbar Component
 *
 * Dual-mode snackbar component supporting both service-based and declarative usage. Follows Moon Design System
 * specifications with Angular Material architecture patterns.
 *
 * @example
 *   // Service-based (programmatic)
 *   this.snackbar.show({ message: 'Saved!', context: 'positive' });
 *
 * @example
 *   // Declarative (template)
 *   <hx-snackbar [isOpen]="showSuccess" message="Saved!" context="positive"></hx-snackbar>
 */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { HxBaseControl } from '../../base/hx-base-control';
import { HxIcon } from '../icon/icon';
import { SnackbarContext, SnackbarVariant } from './snackbar-config';
import { HxSnackbarData } from './snackbar-data';

@Component({
  selector: 'hx-snackbar',
  standalone: true,
  imports: [CommonModule, HxIcon],
  templateUrl: './snackbar.html',
  styleUrls: ['./_snackbar-base.css', './snackbar-tokens.css', './snackbar.css'],
  host: {
    '[attr.role]': 'ariaRole',
    '[attr.aria-live]': 'ariaLive',
    '[attr.aria-atomic]': 'true',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxSnackbar extends HxBaseControl implements OnDestroy, OnInit {
  private readonly ngZone = inject(NgZone);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  /** Internal data structure with display properties. Used in service-based mode. */
  data?: HxSnackbarData;

  /** Emits when the enter animation completes. Used by the container to wire afterOpened(). @internal */
  readonly _onEnter = new Subject<void>();

  // ============================================
  // Declarative Mode Inputs
  // ============================================

  /** Controls visibility (declarative mode) */
  readonly isOpen = input<boolean>(false);

  /** Notification message text */
  readonly message = input('');

  /** Visual weight variant */
  readonly variant = input<SnackbarVariant>('fill');

  /** Semantic context for color theming */
  readonly context = input<SnackbarContext>('neutral');

  /** Auto-dismiss duration in milliseconds (0 = no auto-dismiss) */
  readonly duration = input(3000);

  /** Show context icon */
  readonly hasIcon = input(false);

  /** Show close button */
  readonly hasClose = input(false);

  /** Show action button */
  readonly hasAction = input(false);

  /** Action button label */
  readonly actionLabel = input('');

  /** Custom CSS class */
  readonly customClass = input('');

  /** Icon class (optional override) */
  readonly icon = input('');

  /** Custom announcement message for screen readers */
  readonly announcementMessage = input('');

  // ============================================
  // Declarative Mode Outputs
  // ============================================

  /** Emitted when snackbar is dismissed */
  @Output() readonly dismissed = new EventEmitter<void>();

  /** Emitted when action button is clicked */
  @Output() readonly actionClicked = new EventEmitter<void>();

  // ============================================
  // Internal State
  // ============================================

  /** Animation state signal */
  readonly animationState = signal<'void' | 'visible' | 'hidden'>('void');

  /** Tracks if component is in declarative mode (has @Input bindings) */
  private readonly isDeclarativeMode = signal(false);

  /** Auto-dismiss timer */
  private autoDismissTimer?: ReturnType<typeof setTimeout>;

  /** Effective auto-dismiss duration in ms for restart behavior */
  private effectiveAutoDismissMs = 0;

  /** Fallback: fires if CSS enter animation is suppressed (noop animations, jest) @internal */
  _enterFallback?: ReturnType<typeof setTimeout>;

  /** Fallback: fires if CSS exit animation is suppressed (noop animations, jest) @internal */
  _exitFallback?: ReturnType<typeof setTimeout>;

  /** Get effective config (from data or inputs) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get config(): any {
    if (this.data) {
      const fallbackMessage =
        this.data.message && this.data.message.trim().length > 0 ? this.data.message : 'Notification';
      return {
        message: fallbackMessage,
        variant: this.data.variant,
        context: this.data.context,
        duration: 0, // Service-based mode: container manages auto-dismiss via afterOpened()
        hasIcon: this.data.hasIcon,
        hasClose: this.data.hasClose,
        hasAction: this.data.hasAction,
        actionLabel: this.data.actionLabel,
        customClass: this.data.customClass,
        icon: this.data.icon,
        ariaLive: this.data.ariaLive,
        announcementMessage: this.data.announcementMessage || fallbackMessage,
      };
    }
    const msg = this.message();
    const fallbackMessage = msg && msg.trim().length > 0 ? msg : 'Notification';
    const ctx = this.context();
    const annMsg = this.announcementMessage();
    return {
      message: fallbackMessage,
      variant: this.variant(),
      context: ctx,
      duration: this.duration(),
      hasIcon: this.hasIcon(),
      hasClose: this.hasClose(),
      hasAction: this.hasAction(),
      actionLabel: this.actionLabel(),
      customClass: this.customClass(),
      icon: this.icon(),
      ariaLive: ctx === 'negative' || ctx === 'caution' ? ('assertive' as const) : ('polite' as const),
      announcementMessage: annMsg || fallbackMessage,
    };
  }

  /** ARIA role based on context */
  get ariaRole(): 'status' | 'alert' {
    const ctx = this.data?.context || this.context();
    return ctx === 'negative' || ctx === 'caution' ? 'alert' : 'status';
  }

  /** ARIA live politeness */
  get ariaLive(): 'polite' | 'assertive' {
    return this.config.ariaLive;
  }

  /** Computed CSS classes for variant, context, and animation state */
  @HostBinding('class')
  get hostClasses(): string {
    const cfg = this.config;
    const state = this.animationState();
    const classes = ['hx-snackbar', `hx-snackbar-${cfg.variant}`, `hx-snackbar-${cfg.context}`];

    if (state === 'visible') {
      classes.push('hx-snackbar--visible');
    } else if (state === 'hidden') {
      classes.push('hx-snackbar--hidden');
    }

    if (cfg.customClass) {
      classes.push(cfg.customClass);
    }

    return classes.join(' ');
  }

  /** Handle native CSS animationend and animationcancel events */
  @HostListener('animationend', ['$event'])
  @HostListener('animationcancel', ['$event'])
  onNativeAnimationEnd(event: Event): void {
    const animEvent = event as globalThis.AnimationEvent;
    // Guard: only process events from this host element, not child element bubbles
    if (animEvent.target !== this.elementRef.nativeElement) {
      return;
    }
    if (animEvent.animationName === 'hx-snackbar-enter') {
      this.onEnterAnimationDone();
    } else if (animEvent.animationName === 'hx-snackbar-exit') {
      this.onExitAnimationDone();
    }
  }

  /** Listen for Escape key to dismiss */
  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    this.dismiss();
  }

  /** Pause auto-dismiss while pointer is over snackbar */
  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.pauseAutoDismiss();
  }

  /** Resume auto-dismiss when pointer leaves snackbar */
  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.resumeAutoDismiss();
  }

  /** Pause auto-dismiss while snackbar (or children) has keyboard focus */
  @HostListener('focusin')
  onFocusIn(): void {
    this.pauseAutoDismiss();
  }

  /** Resume auto-dismiss when focus exits snackbar subtree */
  @HostListener('focusout', ['$event'])
  onFocusOut(event: FocusEvent): void {
    const hostEl = this.elementRef.nativeElement;
    const nextTarget = event.relatedTarget as Node | null;
    if (!nextTarget || !hostEl.contains(nextTarget)) {
      this.resumeAutoDismiss();
    }
  }

  /** Clicking active snackbar should keep it visible by restarting auto-dismiss timer */
  @HostListener('click')
  onHostClick(): void {
    this.restartAutoDismiss();
  }

  constructor() {
    super();
    // React to isOpen signal changes. Guard with isDeclarativeMode() so this does not
    // fire in service mode (where data is set instead of isOpen). isDeclarativeMode is
    // set in ngOnInit, so the effect is dormant until then.
    effect(() => {
      if (!this.isDeclarativeMode()) {
        return;
      }
      if (this.isOpen()) {
        this.showSnackbar();
      } else {
        this.hide();
      }
    });
  }

  /** Lifecycle hook: trigger entrance animation after component initialisation */
  ngOnInit(): void {
    // Detect if declarative mode (no injected data)
    this.isDeclarativeMode.set(!this.data);

    // Service-based mode: start entrance animation immediately.
    // Declarative mode: the effect() reacts to isOpen() once isDeclarativeMode is set above.
    if (this.data) {
      setTimeout(() => {
        this.animationState.set('visible');
        clearTimeout(this._enterFallback);
        this._enterFallback = setTimeout(() => {
          this.onEnterAnimationDone();
        }, 200);
      }, 0);
    }
  }

  /** Cleanup */
  ngOnDestroy(): void {
    this.clearAutoDismissTimer();
    clearTimeout(this._enterFallback);
    clearTimeout(this._exitFallback);
    if (!this._onEnter.closed) {
      this._onEnter.complete();
    }
  }

  /** Show snackbar (declarative mode) */
  private showSnackbar(): void {
    this.clearAutoDismissTimer();
    this.animationState.set('visible');
    clearTimeout(this._enterFallback);
    this._enterFallback = setTimeout(() => {
      this.onEnterAnimationDone();
    }, 200);
  }

  /** Hide snackbar (declarative mode) */
  private hide(): void {
    this.clearAutoDismissTimer();
    this.animationState.set('hidden');
    clearTimeout(this._exitFallback);
    this._exitFallback = setTimeout(() => {
      this.onExitAnimationDone();
    }, 200);
  }

  /** Called when enter animation completes (or 200ms fallback fires) */
  private onEnterAnimationDone(): void {
    clearTimeout(this._enterFallback);
    this._enterFallback = undefined;
    if (!this._onEnter.closed) {
      this._onEnter.next();
      this._onEnter.complete();
    }
    // Declarative mode only: start auto-dismiss here.
    // Service mode uses afterOpened() in the container.
    if (this.isDeclarativeMode()) {
      this.startAutoDismissTimer();
    }
  }

  /** Called when exit animation completes (or 200ms fallback fires) */
  private onExitAnimationDone(): void {
    clearTimeout(this._exitFallback);
    this._exitFallback = undefined;
    // Service-based mode: trigger overlay cleanup and complete afterDismissed()
    if (this.data?.snackbarRef) {
      this.data.snackbarRef._finishDismiss();
    }
    // Declarative mode: emit dismissed event
    if (this.isDeclarativeMode()) {
      this.dismissed.emit();
    }
  }

  /** Clamp duration between 2000 and 10000ms, but allow 0 for manual-only */
  private clampDuration(duration?: number): number {
    if (duration === 0) {
      return 0;
    }
    if (typeof duration !== 'number' || isNaN(duration)) {
      return 4000;
    }
    return Math.max(2000, Math.min(10000, duration));
  }

  /** Start auto-dismiss timer */
  private startAutoDismissTimer(): void {
    const duration = this.clampDuration(this.config.duration);
    this.effectiveAutoDismissMs = duration;
    if (duration > 0) {
      this.autoDismissTimer = setTimeout(() => {
        this.dismiss();
      }, duration);
    }
  }

  /** Clear auto-dismiss timer */
  private clearAutoDismissTimer(): void {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = undefined;
    }
  }

  /** Pause auto-dismiss timer for both declarative and service-based modes */
  private pauseAutoDismiss(): void {
    if (this.data?.snackbarRef) {
      this.data.snackbarRef._pauseAutoDismissTimer();
      return;
    }
    this.clearAutoDismissTimer();
  }

  /** Resume auto-dismiss timer for both declarative and service-based modes */
  private resumeAutoDismiss(): void {
    if (this.data?.snackbarRef) {
      this.data.snackbarRef._resumeAutoDismissTimer();
      return;
    }

    if (this.isDeclarativeMode() && this.animationState() === 'visible' && this.effectiveAutoDismissMs > 0) {
      this.clearAutoDismissTimer();
      this.autoDismissTimer = setTimeout(() => {
        this.dismiss();
      }, this.effectiveAutoDismissMs);
    }
  }

  /** Restart auto-dismiss timer from full duration when snackbar is clicked */
  private restartAutoDismiss(): void {
    if (this.data?.snackbarRef) {
      this.data.snackbarRef._restartAutoDismissTimer();
      return;
    }

    if (this.isDeclarativeMode() && this.animationState() === 'visible' && this.effectiveAutoDismissMs > 0) {
      this.clearAutoDismissTimer();
      this.autoDismissTimer = setTimeout(() => {
        this.dismiss();
      }, this.effectiveAutoDismissMs);
    }
  }

  /** Dismiss the snackbar */
  dismiss(): void {
    this.clearAutoDismissTimer();

    // Service-based mode: delegate to ref which calls startExit()
    if (this.data?.snackbarRef) {
      this.data.snackbarRef.dismiss();
      return;
    }

    // Declarative mode: trigger exit animation + fallback
    this.animationState.set('hidden');
    clearTimeout(this._exitFallback);
    this._exitFallback = setTimeout(() => {
      this.onExitAnimationDone();
    }, 200);
  }

  /** Handle action button click */
  onAction(): void {
    this.dismiss();

    // Service-based mode: Execute callback
    if (this.data?.onAction) {
      setTimeout(async () => {
        await this.data?.onAction?.();
      }, 0);
    }

    // Declarative mode: Emit event
    if (this.isDeclarativeMode()) {
      this.actionClicked.emit();
    }
  }

  /**
   * Trigger exit animation. Called by HxSnackbarRef.dismiss(). Wrapped in NgZone.run() so change detection fires when
   * called from outside Angular's zone (e.g. HTTP callbacks, zone-less contexts).
   *
   * @internal
   */
  startExit(): void {
    this.ngZone.run(() => {
      this.animationState.set('hidden');
      clearTimeout(this._exitFallback);
      this._exitFallback = setTimeout(() => {
        this.onExitAnimationDone();
      }, 200);
    });
  }
}
